import React, { useCallback } from "react";
import { ReactNode, isValidElement, useMemo } from "react";

type EditorNode =
  | React.ReactPortal
  | React.ReactElement<unknown, string | React.JSXElementConstructor<any>>;

type Input = {
  name: string;
  title: string;
  required: boolean;
};

export type EditorSection = {
  title: string;
  inputs: Input[];
};

/**
 * Traverses through editor layout children to gather a collection
 * of the sections and their relevant inputs
 */
const useEditorSections = (children: ReactNode) => {
  /**
   * Searches an inputs children for the `name` attribute if
   * it can't find it in the `Input` parent
   */
  const findDeepInputName = (input: EditorNode) => {
    if (input.props.children === undefined) return undefined;

    const child = React.Children.toArray(input.props.children).find(child => {
      if (isValidElement(child) && "props" in child) {
        if (child.props.name) return true;
      }
      return false;
    });

    if (child && isValidElement(child)) return child.props.name;
  };

  /**
   * Gather the information for each of the
   */
  const findSectionInputs = useCallback(
    (section: EditorNode) =>
      React.Children.map(section, child => {
        if (isValidElement(child) && "props" in child) {
          if (child.props.inputTitle) {
            const name =
              (child.props.name as string | undefined) ||
              findDeepInputName(child);

            if (name) {
              return {
                name,
                title: child.props.inputTitle,
                required:
                  child.props.required !== undefined
                    ? child.props.required
                    : false
              } as Input;
            }
          }
        }
        return undefined;
      }).filter(child => child !== undefined),
    []
  );

  /**
   * Retrieve the editor sections & inputs from the child elements
   */
  const sections = useMemo(() => {
    return React.Children.map(children, child => {
      if (isValidElement(child) && "props" in child) {
        if (child.props.sectionTitle !== undefined && child.props.children) {
          const inputs = findSectionInputs(child.props.children);

          return {
            title: child.props.sectionTitle,
            inputs
          } as EditorSection;
        }
      }

      return undefined;
    });
  }, [children, findSectionInputs]);

  return sections || [];
};

export default useEditorSections;
